import React, { useRef, useState } from 'react';
import { Dropdown, SearchBox } from 'tea-component';
import { useHistory, getLocale, useIntl } from 'umi';
import { useMenuSearch, highlightText } from './hooks/useMenuSearch';
import type { SearchResult, MenuItem } from './types';
import { NO_DATE } from '@op/constants';
import './index.scss';

type MenuSearchProps = {
  listMenu: MenuItem[];
  wholeGameInfo: {
    edition_unified_id?: string;
    [key: string]: any;
  };
};

export default function MenuSearch({ listMenu, wholeGameInfo }: MenuSearchProps) {
  const history = useHistory();
  const locale = getLocale();
  const { formatMessage } = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const { keyword, setKeyword, searchResults } = useMenuSearch(listMenu);

  // 选择菜单项
  const handleSelectItem = (item: SearchResult) => {
    const edition_unified_id = wholeGameInfo?.edition_unified_id;
    const linkUrl = item?.actualPath?.includes?.('?') ? '&' : '?';
    const fullUrl = `${item.actualPath}${linkUrl}gameid=${edition_unified_id}`;

    setKeyword('');
    setShowDropdown(false);
    history.push(fullUrl);
  };

  // 搜索框内容变化
  const handleSearchChange = (val: string) => {
    setKeyword(val);
    if (val.trim()) {
      setShowDropdown(true);
    }
  };

  // 回车选择第一个结果
  const handlePressEnter = () => {
    if (searchResults.length > 0) {
      handleSelectItem(searchResults[0]);
    }
  };

  // 渲染搜索结果项
  const renderResultItem = (item: SearchResult) => {
    const displayPath = (locale === 'en-US' ? item.breadcrumbs_en : item.breadcrumbs).join('/');

    return (
      <div
        key={item.id}
        className="dbo-navmenu-search-item"
        onClick={() => handleSelectItem(item)}
      >
        <div className="dbo-navmenu-search-item-content">
          <div className="dbo-navmenu-search-item-path">{highlightText(displayPath, keyword)}</div>
        </div>
      </div>
    );
  };

  // 下拉框内容
  const renderDropdownContent = () => {
    if (!keyword.trim()) {
      return null;
    }

    if (searchResults.length === 0) {
      return (
        <div className="dbo-navmenu-search-empty">
          <div className="dbo-navmenu-search-empty-text">
            {formatMessage({ id: NO_DATE.SIMPLE })}
          </div>
        </div>
      );
    }

    return (
      <div className="dbo-navmenu-search-list">
        {searchResults.map((item) => renderResultItem(item))}
      </div>
    );
  };

  return (
    <div ref={containerRef} className="dbo-navmenu-hd-search">
      <Dropdown
        className="dbo-navmenu-search-dropdown"
        appearance="pure"
        placement="bottom-start"
        open={showDropdown && !!keyword.trim()}
        onOpenChange={(open) => {
          if (!open) {
            setShowDropdown(false);
          }
        }}
        closeOnScroll={true}
        matchButtonWidth={false}
        button={
          <SearchBox
            value={keyword}
            onChange={handleSearchChange}
            onPressEnter={handlePressEnter}
            onFocus={() => {
              if (keyword.trim()) {
                setShowDropdown(true);
              }
            }}
            placeholder={formatMessage({ id: 'Search pages' })}
            className="dbo-navmenu-search-input"
          />
        }
      >
        {renderDropdownContent()}
      </Dropdown>
    </div>
  );
}
